import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material';
import { Store, Save } from '@mui/icons-material';
import { storeService } from '../services/storeService';
import { AppHeader } from '../components/AppHeader';

interface StoreFormData {
  name: string;
  cnpj: string;
  address: string;
  phone: string;
  email: string;
}

const storeSchema: yup.ObjectSchema<StoreFormData> = yup.object({
  name: yup.string().required('Nome da loja é obrigatório'),
  cnpj: yup.string()
    .matches(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, {
      message: 'CNPJ deve estar no formato 00.000.000/0000-00',
      excludeEmptyString: true,
    })
    .default(''),
  address: yup.string().default(''),
  phone: yup.string()
    .matches(/^\(\d{2}\)\s?\d{4,5}-\d{4}$/, {
      message: 'Telefone deve estar no formato (00) 00000-0000',
      excludeEmptyString: true,
    })
    .default(''),
  email: yup.string().email('Email inválido').default('')
});

const StoreForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const isEdit = !!id;

  const { control, handleSubmit, reset, formState: { errors } } = useForm<StoreFormData>({
    resolver: yupResolver(storeSchema),
    defaultValues: {
      name: '',
      cnpj: '',
      address: '',
      phone: '',
      email: ''
    }
  });

  const fetchStore = useCallback(async (storeId: string) => {
    try {
      const store = await storeService.getStoreById(storeId);
      reset({
        name: store.name,
        cnpj: store.cnpj,
        address: store.address,
        phone: store.phone,
        email: store.email
      });
    } catch (error) {
      console.error('Erro ao carregar loja:', error);
      setError('Erro ao carregar dados da loja');
    }
  }, [reset]);

  useEffect(() => {
    if (isEdit && id) {
      fetchStore(id);
    }
  }, [isEdit, id, fetchStore]);

  const onSubmit: SubmitHandler<StoreFormData> = async (data) => {
    try {
      setLoading(true);
      setError('');

      if (isEdit && id) {
        await storeService.updateStore(id, data);
      } else {
        await storeService.createStore(data);
      }

      navigate('/stores');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar loja');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppHeader 
        title={isEdit ? 'Editar Loja' : 'Nova Loja'}
        subtitle={isEdit ? 'Atualize as informações da loja' : 'Cadastre uma nova loja participante'}
        icon={<Store />}
        backTo="/stores"
      />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, elevation: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            {isEdit ? 'Editar Loja' : 'Nova Loja'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              {/* Informações Básicas */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Informações da Loja
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome da Loja"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={{ borderRadius: 2 }}
                  />
                )}
              />

              <Controller
                name="cnpj"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="CNPJ"
                    placeholder="00.000.000/0000-00"
                    error={!!errors.cnpj}
                    helperText={errors.cnpj?.message}
                    sx={{ borderRadius: 2 }}
                  />
                )}
              />

              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Endereço"
                    multiline
                    rows={2}
                    error={!!errors.address}
                    helperText={errors.address?.message}
                    sx={{ borderRadius: 2 }}
                  />
                )}
              />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Telefone"
                        placeholder="(00) 00000-0000"
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                        sx={{ borderRadius: 2 }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email"
                        type="email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        sx={{ borderRadius: 2 }}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Botões de Ação */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/stores')}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  {loading ? 'Salvando...' : (isEdit ? 'Atualizar' : 'Criar Loja')}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default StoreForm; 